import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";

import { API_BASE_URL } from "../../config";
import useToast from "../../components/useToast";
import AppButton from "../../design-system/components/AppButton";
import AppCallout from "../../design-system/components/AppCallout";
import AppCard from "../../design-system/components/AppCard";
import AppCardBody from "../../design-system/components/AppCardBody";
import AppChip from "../../design-system/components/AppChip";
import AppField from "../../design-system/components/AppField";
import AppFieldStack from "../../design-system/components/AppFieldStack";
import AppInput from "../../design-system/components/AppInput";
import AppLoadingOverlay from "../../design-system/components/AppLoadingOverlay";
import AppPage from "../../design-system/components/AppPage";
import AppSelect from "../../design-system/components/AppSelect";
import AppSwitch from "../../design-system/components/AppSwitch";
import AppText from "../../design-system/components/AppText";
import AppTextarea from "../../design-system/components/AppTextarea";
import AppWorkspace from "../../design-system/components/AppWorkspace";
import { IconChevronDown, IconChevronUp } from "../../design-system/icons";

// Anzeige-Reihenfolge der weichen Gruppen (AE-028)
const GROUP_ORDER = ["besetzung", "fairness", "wie_bisher", "teams", "pensen"];

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("session_token")}`,
});

const stop = (event) => event.stopPropagation();

// Geltungsbereich "ganze Firma" im Bereichs-Select (AE-048)
const COMPANY_SCOPE = "company";

// Aufklappbare Detail-Flaeche unter der Zeile — bewusst lokale Abweichung,
// AppTable kennt keine Akkordeon-Zeilen (Design-System bleibt unangetastet).
const DETAIL_PANEL_STYLE = {
  padding: "10px 16px 16px 44px",
  background: "var(--ds-color-surface-sunken, rgba(0,0,0,0.02))",
  borderBottom: "1px solid var(--ds-color-border-subtle, rgba(0,0,0,0.08))",
};

// Deutsche Labels für die Katalog-Parameter; unbekannte Keys zeigen den Key.
const PARAM_LABELS = {
  max_consecutive_days: "Max. Arbeitstage in Folge",
  min_rest_hours: "Mindestruhezeit (Stunden)",
  max_hours_per_week: "Max. Stunden pro Woche",
  max_shifts_per_day: "Max. Schichten pro Tag",
  default_max_weekends: "Max. Wochenenden pro Monat (für alle MA)",
  user_limits: "Ausnahmen pro Mitarbeiter",
  department_weights: "Ausnahmegewichte pro Department (JSON: {\"DepartmentID\": Gewicht})",
  department_minimums: "Mindestbesetzung pro Department — bis dahin gilt das Ausnahmegewicht (JSON: {\"DepartmentID\": Anzahl})",
  user_weights: "Eigenes Gewicht pro Mitarbeiter (JSON: {\"UserID\": Gewicht})",
  department_ids: "Departments",
  trigger_department_ids: "Auslösende Departments",
  companion_department_ids: "Begleit-Departments (am selben Tag zusätzlich)",
  overlap_exempt_department_ids: "Departments ohne Überlappungsprüfung",
  allowed_department_ids: "Departments, in denen die Absenz fällt (leer = alle)",
  short_absence_minutes: "Absenzen bis zu so vielen Minuten fallen in allen Departments (0 = aus)",
  block_whole_shift: "Absenz über die ganze Schicht sperrt diese Schicht",
  shift_ids: "Schichten",
  day_types: "Welche Spezialtage zählen",
  early_end_hour: "Früh endet um (Stunde, z. B. 12)",
  lookback_weeks: "Historie-Fenster (Wochen)",
  lookback_days: "Historie-Fenster (Tage, z. B. 180 = ein halbes Jahr)",
  separate_lookback_days: "Historie-Fenster der separat gezählten Wochentage (Tage, z. B. 365)",
  absence_gap_days: "Ab so vielen Tagen ohne Einsatz gilt jemand als abwesend und startet beim Schnitt (0 = aus)",
  separate_weekdays: "Wochentage mit eigener Zählung (0 = Mo … 5 = Sa, 6 = So)",
  max_level: "Ab diesem Vorsprung steigt der Preis nicht weiter (Deckel, 0 = kein Deckel)",
  max_per_week: "Max. Einsätze pro Woche",
  max_per_month: "Max. Einsätze pro Kalendermonat (0 = aus)",
  min_per_month: "Min. Einsätze pro Kalendermonat (0 = aus, nur für Monate, die im Lauf enden)",
  ignore_department_ids: "Departments, die nicht mitzählen",
  from_department_ids: "Nach diesen Departments …",
  to_department_ids: "… soll eines dieser Departments folgen",
  slot_shift_ids: "Halbtags-Schichten (Reihenfolge nach Startzeit)",
  source_department_ids: "Herkunfts-Departments (am selben Tag)",
  min_from_source: "Mindestens so viele Personen aus den Herkunfts-Departments",
  excluded_department_ids: "Ausgeschlossene Departments (am selben Tag)",
  tasks: "Kontingente pro Department (JSON: {\"DepartmentID\": {\"half_days\": 8, \"per_person\": 4, \"shift_ids\": [13, 14], \"first_days\": 0, \"prefer_full_days\": true, \"prefer_together\": true}})",
  full_day_percent: "Ganze Tage bevorzugen: Kosten pro fehlendem Halbtag (% des Gewichts)",
  together_percent: "Gemeinsame Tage bevorzugen: Kosten pro Aufgabentag (% des Gewichts)",
  full_day_follow_percent: "Nach einem ganzen Tag: Kosten pro weiterem Halbtag am Folgetag (% des Gewichts)",
  as_group: "Departments als eine Gruppe zählen (Wechsel zwischen ihnen gilt als Folgetag)",
  priority_department_ids: "Prioritäts-Departments (bekommen die überzählige Person zuerst)",
  repeat_surcharge_percent: "Aufschlag pro weiterem Wechseltag derselben Person (% des Gewichts)",
  max_gap_min: "Max. Lücke (Minuten)",
  evening_start_min: "Abend beginnt (Minuten seit 00:00, z. B. 990 = 16:30)",
  block_days: "Blocklänge (Tage)",
  islands: "Rotationsgruppen",
  days_after: "Geschützte Tage nach den Ferien",
  days_before: "Geschützte Tage vor den Ferien",
  min_holiday_days: "Regel gilt ab Ferienlänge (Tage)",
  workdays_only: "Nur Arbeitstage zählen (Mo–Fr ohne Feiertage)",
  mode: "Modus",
  cost_form: "Kostenform",
};

// Feste Auswahl-Optionen für Enum-Parameter
const ENUM_OPTIONS = {
  mode: [
    { value: "week", label: "Pro Woche" },
    { value: "period", label: "Ganzer Zeitraum" },
  ],
  cost_form: [
    { value: "linear", label: "Linear" },
    { value: "progressive", label: "Progressiv (grosse Abweichungen teurer)" },
  ],
  day_types: [
    { value: "public_holiday", label: "Feiertag" },
    { value: "company_closed", label: "Betrieb geschlossen" },
    { value: "company_vacation", label: "Betriebsferien" },
    { value: "special_opening", label: "Sonderöffnung" },
  ],
};

const isPlainObject = (value) =>
  value !== null && typeof value === "object" && !Array.isArray(value);

// Editor-Typ pro Parameter: erst bekannte Keys, dann Typ des Defaults
function paramKind(key, defaultValue, customValue) {
  if (key === "user_limits") return "user_limits";
  if (key === "islands") return "islands";
  if (key === "department_ids") return "dept_multi";
  if (key === "trigger_department_ids" || key === "companion_department_ids") return "dept_multi";
  if (key === "overlap_exempt_department_ids") return "dept_multi";
  if (key === "allowed_department_ids") return "dept_multi";
  if (key === "priority_department_ids") return "dept_multi";
  if (key === "ignore_department_ids") return "dept_multi";
  if (key === "source_department_ids" || key === "excluded_department_ids") return "dept_multi";
  if (key === "from_department_ids" || key === "to_department_ids") return "dept_multi";
  if (key === "slot_shift_ids") return "shift_multi";
  if (key === "shift_ids") return "shift_multi";
  if (key === "day_types") return "day_types";
  if (key === "mode" || key === "cost_form") return "enum";
  const ref = defaultValue !== undefined && defaultValue !== null ? defaultValue : customValue;
  if (typeof ref === "boolean") return "bool";
  if (Array.isArray(ref)) return "list";
  if (isPlainObject(ref)) return "json";
  if (typeof ref === "string") return "text";
  return "number"; // Zahlen und null-Defaults (z. B. default_max_weekends)
}

function fieldValueFor(kind, customValue) {
  if (kind === "user_limits") {
    return Object.entries(customValue || {}).map(([userId, limit]) => ({
      userId: Number(userId),
      limitText: limit === null || limit === undefined ? "" : String(limit),
    }));
  }
  if (kind === "islands") {
    return Object.entries(customValue || {})
      .map(([nr, deptIds]) => ({
        nrText: String(nr),
        departmentIds: (deptIds || []).map(Number),
      }))
      .sort((a, b) => Number(a.nrText) - Number(b.nrText));
  }
  if (kind === "dept_multi" || kind === "shift_multi") {
    return (customValue || []).map(Number);
  }
  if (kind === "day_types") {
    return (customValue || []).map(String);
  }
  if (customValue === undefined || customValue === null) return kind === "bool" ? null : "";
  if (kind === "bool") return Boolean(customValue);
  if (kind === "list") return Array.isArray(customValue) ? customValue.join(", ") : String(customValue);
  if (kind === "json") return JSON.stringify(customValue);
  return String(customValue);
}

function placeholderFor(kind, defaultValue) {
  if (defaultValue === undefined || defaultValue === null) return "leer = kein Limit";
  if (kind === "list") return Array.isArray(defaultValue) && defaultValue.length ? defaultValue.join(", ") : "leer = keine";
  if (kind === "json") return JSON.stringify(defaultValue);
  return String(defaultValue);
}

// Beschreibt alle Parameter einer NB: Katalog-Defaults + evtl. Extra-Keys des Kunden
function paramDescriptors(def, customParamsOrFields) {
  const keys = [...Object.keys(def.default_params || {})];
  Object.keys(customParamsOrFields || {}).forEach((key) => {
    if (!keys.includes(key)) keys.push(key);
  });
  return keys.map((key) => {
    const defaultValue = (def.default_params || {})[key];
    const customValue = (customParamsOrFields || {})[key];
    const kind = paramKind(key, defaultValue, customValue);
    return { key, kind, defaultValue };
  });
}

// Sicht eines Geltungsbereichs (AE-048): firmenweit ("") oder ein Standort.
// Im Standort ohne eigene Zeile gilt die Firmen-Zeile — sie wird als
// Ausgangswert gezeigt (inherited), gespeichert entsteht eine Standort-Zeile.
function draftFor(def, ownRow, companyRow) {
  const setting = ownRow || companyRow;
  const customParams = setting && setting.params ? setting.params : {};
  const fields = {};
  paramDescriptors(def, customParams).forEach(({ key, kind }) => {
    fields[key] = fieldValueFor(kind, customParams[key]);
  });
  return {
    hasRow: !!ownRow,
    inherited: !ownRow && !!companyRow,
    // Ohne Zeile gilt der Katalog-Default: harte Standard-NBs an, Kunden-NBs
    // aus (Registry default_active); aeltere Backends liefern das Feld nicht.
    active: setting ? !!setting.is_active : (def.default_active ?? def.kind === "hard"),
    weightText:
      setting && setting.weight_override !== null && setting.weight_override !== undefined
        ? String(setting.weight_override)
        : "",
    fields,
    _dirty: false,
  };
}

// Formularfelder -> params-Objekt; leere Felder = Katalog-Default (Key weggelassen)
function parseParams(def, draft) {
  const params = {};
  for (const { key, kind } of paramDescriptors(def, draft.fields)) {
    const raw = draft.fields[key];
    const label = PARAM_LABELS[key] || key;

    if (kind === "user_limits") {
      const rows = (raw || []).filter((r) => r.userId);
      const seen = new Set();
      const result = {};
      for (const row of rows) {
        if (seen.has(row.userId)) {
          return { error: `${label}: Mitarbeiter doppelt aufgeführt` };
        }
        seen.add(row.userId);
        const text = String(row.limitText ?? "").trim();
        if (text === "") {
          result[String(row.userId)] = null; // unbegrenzt für diesen MA
        } else {
          const value = Number(text);
          if (!Number.isInteger(value) || value < 0) {
            return { error: `${label}: Anzahl Wochenenden muss eine ganze Zahl >= 0 sein` };
          }
          result[String(row.userId)] = value;
        }
      }
      if (Object.keys(result).length) params[key] = result;
      continue;
    }

    if (kind === "islands") {
      const rows = (raw || []).filter((r) => String(r.nrText ?? "").trim() || (r.departmentIds || []).length);
      const seen = new Set();
      const result = {};
      for (const row of rows) {
        const nr = Number(String(row.nrText ?? "").trim());
        if (!Number.isInteger(nr) || nr <= 0) {
          return { error: `${label}: Gruppen-Nr muss eine ganze Zahl > 0 sein` };
        }
        if (seen.has(nr)) {
          return { error: `${label}: Gruppen-Nr ${nr} doppelt` };
        }
        if (!(row.departmentIds || []).length) {
          return { error: `${label}: Gruppe ${nr} braucht mindestens ein Department` };
        }
        seen.add(nr);
        result[String(nr)] = row.departmentIds.map(Number);
      }
      if (Object.keys(result).length) params[key] = result;
      continue;
    }

    if (kind === "dept_multi" || kind === "shift_multi" || kind === "day_types") {
      const values = raw || [];
      if (values.length) params[key] = kind === "day_types" ? values.map(String) : values.map(Number);
      continue;
    }

    if (kind === "enum") {
      const text = String(raw ?? "").trim();
      if (text) params[key] = text;
      continue;
    }

    if (kind === "bool") {
      if (raw === null || raw === undefined) continue;
      params[key] = Boolean(raw);
      continue;
    }

    const text = String(raw ?? "").trim();
    if (!text) continue;
    if (kind === "number") {
      const value = Number(text);
      if (!Number.isFinite(value)) {
        return { error: `${label}: bitte eine Zahl eingeben` };
      }
      params[key] = value;
    } else if (kind === "list") {
      const tokens = text.split(",").map((t) => t.trim()).filter(Boolean);
      const numeric = tokens.every((t) => /^-?\d+$/.test(t));
      params[key] = numeric ? tokens.map(Number) : tokens;
    } else if (kind === "json") {
      try {
        const value = JSON.parse(text);
        if (!isPlainObject(value)) {
          return { error: `${label}: JSON-Objekt erwartet` };
        }
        params[key] = value;
      } catch {
        return { error: `${label}: kein gültiges JSON` };
      }
    } else {
      params[key] = text;
    }
  }
  return { params: Object.keys(params).length ? params : null };
}

function parseDraft(def, draft) {
  const parsedParams = parseParams(def, draft);
  if (parsedParams.error) {
    return { error: `NB ${def.number} — ${parsedParams.error}` };
  }
  const weightText = (draft.weightText || "").trim();
  const weight = weightText === "" ? null : Number(weightText);
  if (weight !== null && (!Number.isFinite(weight) || weight < 0)) {
    return { error: `NB ${def.number}: Gewicht muss eine Zahl >= 0 sein` };
  }
  return {
    payload: {
      nb_number: def.number,
      is_active: draft.active,
      weight_override: def.kind === "soft" ? weight : null,
      params: parsedParams.params,
    },
  };
}

/** Tabellen-Optik des Design-Systems (ds-table-Klassen), aber mit
 *  Akkordeon: die geklickte Zeile klappt ihre Details direkt darunter auf. */
function NbTable({ columns, rows, expandedId, onToggle, renderDetail }) {
  const gridTemplateColumns = columns
    .map((column) => (column.width ? `${column.width}px` : `minmax(${column.minWidth || 200}px, 1fr)`))
    .join(" ");

  return (
    <div className="ds-table ds-table--compact" role="table">
      <div className="ds-table__header">
        <div className="ds-table__header-inner" style={{ gridTemplateColumns, width: "100%" }} role="row">
          {columns.map((column) => (
            <div
              key={column.key}
              role="columnheader"
              className={`ds-table__head ds-table__head--${column.align || "left"}`}
            >
              {column.label}
            </div>
          ))}
        </div>
      </div>
      <div className="ds-table__body" role="rowgroup">
        <div className="ds-table__body-inner" style={{ width: "100%" }}>
          {rows.map((row) => {
            const expanded = expandedId === row.id;
            return (
              <React.Fragment key={row.id}>
                <div
                  className={`ds-table__row ds-table__row--clickable${expanded ? " ds-table__row--active" : ""}`}
                  style={{ gridTemplateColumns }}
                  role="row"
                  tabIndex={0}
                  onClick={() => onToggle(row.id)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      onToggle(row.id);
                    }
                  }}
                >
                  {columns.map((column) => (
                    <div
                      key={column.key}
                      role="cell"
                      className={`ds-table__cell ds-table__cell--${column.align || "left"}`}
                    >
                      <div className={`ds-table__cell-content ds-table__cell-content--${column.align || "left"}`}>
                        {column.render(row, expanded)}
                      </div>
                    </div>
                  ))}
                </div>
                {expanded ? (
                  <div style={DETAIL_PANEL_STYLE} onClick={stop}>
                    {renderDetail(row.def)}
                  </div>
                ) : null}
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default function SolverAdmin() {
  const toast = useToast();
  const toastRef = useRef(toast);
  toastRef.current = toast;

  const [catalog, setCatalog] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [schema, setSchema] = useState("");
  const [locations, setLocations] = useState([]);
  // COMPANY_SCOPE = firmenweit, sonst LocationID als String (AppSelect-Wert)
  const [scope, setScope] = useState(COMPANY_SCOPE);
  const [settings, setSettings] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [directory, setDirectory] = useState({ users: [], departments: [], shifts: [] });
  const [expandedNb, setExpandedNb] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingSettings, setLoadingSettings] = useState(false);
  const [savingNb, setSavingNb] = useState(null);

  // Katalog + Kundenliste einmalig
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [catalogRes, companiesRes] = await Promise.all([
          axios.get(`${API_BASE_URL}/api/admin/solver/catalog`, { headers: authHeaders() }),
          axios.get(`${API_BASE_URL}/api/admin/solver/companies`, { headers: authHeaders() }),
        ]);
        if (!alive) return;
        setCatalog(catalogRes.data);
        setCompanies(companiesRes.data?.companies || []);
      } catch {
        toastRef.current.error?.("Katalog/Kundenliste laden fehlgeschlagen");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const scopeLocationId = scope === COMPANY_SCOPE ? null : Number(scope);

  // Zeilen eines Geltungsbereichs, nach NB-Nummer
  const rowsFor = (allSettings, locationId) => {
    const byNb = {};
    allSettings
      .filter((s) => (s.location_id ?? null) === locationId)
      .forEach((s) => {
        byNb[s.nb_number] = s;
      });
    return byNb;
  };

  const buildDraft = (def, allSettings, locationId) => {
    const own = rowsFor(allSettings, locationId)[def.number];
    const company = locationId === null ? null : rowsFor(allSettings, null)[def.number];
    return draftFor(def, own, company);
  };

  // Settings + Standorte des gewählten Kunden — hängt NUR an schema
  useEffect(() => {
    if (!schema) return undefined;
    let alive = true;
    setLoadingSettings(true);
    setExpandedNb(null);
    setScope(COMPANY_SCOPE);
    (async () => {
      try {
        const [settingsRes, locationsRes] = await Promise.all([
          axios.get(
            `${API_BASE_URL}/api/admin/solver/companies/${encodeURIComponent(schema)}/settings`,
            { headers: authHeaders() }
          ),
          axios.get(
            `${API_BASE_URL}/api/admin/solver/companies/${encodeURIComponent(schema)}/locations`,
            { headers: authHeaders() }
          ),
        ]);
        if (!alive) return;
        setSettings(settingsRes.data?.settings || []);
        setLocations(locationsRes.data?.locations || []);
      } catch {
        if (alive) toastRef.current.error?.("Einstellungen laden fehlgeschlagen");
      } finally {
        if (alive) setLoadingSettings(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [schema]);

  // Entwürfe neu aufbauen, sobald Bereich oder geladene Zeilen wechseln.
  // Bewusst nicht an `settings` gebunden: ein Speichern ersetzt nur den
  // Entwurf der gespeicherten NB, offene Eingaben anderer NBs bleiben.
  useEffect(() => {
    if (!catalog || loadingSettings) return;
    const next = {};
    (catalog.constraints || []).forEach((def) => {
      next[def.number] = buildDraft(def, settings, scopeLocationId);
    });
    setDrafts(next);
    setExpandedNb(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalog, scope, loadingSettings]);

  // Stammdaten der Parameter-Editoren: im Standort nur dessen MA/Departments/Schichten
  useEffect(() => {
    if (!schema) return undefined;
    let alive = true;
    (async () => {
      try {
        const res = await axios.get(
          `${API_BASE_URL}/api/admin/solver/companies/${encodeURIComponent(schema)}/directory`,
          {
            headers: authHeaders(),
            params: scopeLocationId === null ? {} : { location_id: scopeLocationId },
          }
        );
        if (!alive) return;
        setDirectory({
          users: res.data?.users || [],
          departments: res.data?.departments || [],
          shifts: res.data?.shifts || [],
        });
      } catch {
        if (alive) toastRef.current.error?.("Stammdaten laden fehlgeschlagen");
      }
    })();
    return () => {
      alive = false;
    };
  }, [schema, scopeLocationId]);

  const putSettings = async (def, body, successMessage) => {
    setSavingNb(def.number);
    try {
      const res = await axios.put(
        `${API_BASE_URL}/api/admin/solver/companies/${encodeURIComponent(schema)}/settings`,
        { ...body, location_id: scopeLocationId },
        { headers: authHeaders() }
      );
      const nextSettings = res.data?.settings || [];
      setSettings(nextSettings);
      setDrafts((prev) => ({ ...prev, [def.number]: buildDraft(def, nextSettings, scopeLocationId) }));
      toastRef.current.success?.(successMessage);
    } catch (e) {
      toastRef.current.error?.(e?.response?.data?.message || `NB ${def.number}: Speichern fehlgeschlagen`);
    } finally {
      setSavingNb(null);
    }
  };

  const saveDraft = (def, draftOverride) => {
    const draft = draftOverride || drafts[def.number];
    const parsed = parseDraft(def, draft);
    if (parsed.error) {
      toastRef.current.error?.(parsed.error);
      return;
    }
    putSettings(def, { settings: [parsed.payload] }, `NB ${def.number} gespeichert`);
  };

  const toggleActive = (def, checked) => {
    const draft = { ...drafts[def.number], active: checked };
    setDrafts((prev) => ({ ...prev, [def.number]: draft }));
    const parsed = parseDraft(def, draft);
    if (parsed.error) {
      toastRef.current.error?.(parsed.error);
      return;
    }
    putSettings(
      def,
      { settings: [parsed.payload] },
      `NB ${def.number} ${checked ? "aktiviert" : "deaktiviert"}`
    );
  };

  const resetToDefault = (def) => {
    putSettings(
      def,
      { settings: [{ nb_number: def.number, delete: true }] },
      scopeLocationId === null
        ? `NB ${def.number} auf Default zurückgesetzt`
        : `NB ${def.number} folgt wieder der Firmen-Regel`
    );
  };

  const updateDraft = (def, patch) => {
    setDrafts((prev) => ({
      ...prev,
      [def.number]: { ...prev[def.number], ...patch, _dirty: true },
    }));
  };

  const updateField = (def, key, value) => {
    setDrafts((prev) => {
      const current = prev[def.number];
      return {
        ...prev,
        [def.number]: {
          ...current,
          fields: { ...current.fields, [key]: value },
          _dirty: true,
        },
      };
    });
  };

  const hardDefs = useMemo(
    () => (catalog?.constraints || []).filter((d) => d.kind === "hard"),
    [catalog]
  );
  const softByGroup = useMemo(() => {
    const map = {};
    (catalog?.constraints || [])
      .filter((d) => d.kind === "soft")
      .forEach((d) => {
        (map[d.group] = map[d.group] || []).push(d);
      });
    return map;
  }, [catalog]);

  const groupMeta = (key) => (catalog?.groups || []).find((g) => g.key === key) || { label: key };

  const userOptions = useMemo(
    () => directory.users.map((u) => ({ value: u.id, label: u.name })),
    [directory.users]
  );
  const departmentOptions = useMemo(
    () => directory.departments.map((d) => ({ value: d.id, label: d.name })),
    [directory.departments]
  );
  const shiftOptions = useMemo(
    () => directory.shifts.map((s) => ({ value: s.id, label: s.name })),
    [directory.shifts]
  );

  // ---- Spalten ---------------------------------------------------------
  const chevronColumn = {
    key: "chevron",
    label: "",
    width: 36,
    align: "center",
    render: (_row, expanded) =>
      expanded ? (
        <IconChevronUp sx={{ fontSize: 16 }} />
      ) : (
        <IconChevronDown sx={{ fontSize: 16 }} />
      ),
  };

  const nrColumn = {
    key: "number",
    label: "Nr.",
    width: 64,
    render: (row) => <AppText variant="label-1">{row.def.number}</AppText>,
  };

  const nameColumn = {
    key: "name",
    label: "Regel",
    minWidth: 220,
    render: (row) => (
      <span style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
        <AppText variant="body-2" truncate title={row.def.description}>
          {row.def.name}
        </AppText>
        {row.def.quasi_hard ? (
          <AppChip variant="warning" appearance="outlined" size="sm">quasi-hart</AppChip>
        ) : null}
        {row.def.customer_specific ? (
          <AppChip variant="neutral" appearance="outlined" size="sm">
            {row.def.built_for ? `Kunden-NB · ${row.def.built_for}` : "Kunden-NB"}
          </AppChip>
        ) : null}
      </span>
    ),
  };

  const exampleColumn = {
    key: "example",
    label: "Erklärung / Beispiel",
    minWidth: 300,
    render: (row) => (
      <AppText
        variant="body-2"
        tone="secondary"
        style={{ whiteSpace: "normal", lineHeight: 1.35 }}
      >
        {row.def.example}
      </AppText>
    ),
  };

  const statusColumn = {
    key: "status",
    label: "Konfiguration",
    width: 130,
    render: (row) =>
      row.draft.hasRow ? (
        <AppChip variant="info" appearance="outlined" size="sm">
          {scopeLocationId === null ? "angepasst" : "Standort"}
        </AppChip>
      ) : row.draft.inherited ? (
        <AppText variant="body-2" tone="secondary">von Firma</AppText>
      ) : (
        <AppText variant="body-2" tone="secondary">Default</AppText>
      ),
  };

  const weightColumn = {
    key: "weight",
    label: "Gewicht",
    width: 110,
    render: (row) => (
      <span onClick={stop} style={{ display: "block", width: "100%" }}>
        <AppInput
          size="sm"
          type="number"
          value={row.draft.weightText}
          placeholder={String(row.def.default_weight)}
          aria-label={`Gewicht NB ${row.def.number}`}
          onChange={(event) => updateDraft(row.def, { weightText: event.target.value })}
          onKeyDown={(event) => {
            if (event.key === "Enter") event.currentTarget.blur();
          }}
          onBlur={() => {
            if (drafts[row.def.number]?._dirty) saveDraft(row.def);
          }}
        />
      </span>
    ),
  };

  const activeColumn = {
    key: "active",
    label: "Aktiv",
    width: 80,
    align: "center",
    render: (row) => (
      <span onClick={stop}>
        <AppSwitch
          checked={row.draft.active}
          disabled={savingNb === row.def.number}
          aria-label={`NB ${row.def.number} aktiv`}
          onChange={(event) => toggleActive(row.def, event.target.checked)}
        />
      </span>
    ),
  };

  const hardColumns = [chevronColumn, nrColumn, nameColumn, exampleColumn, statusColumn, activeColumn];
  const softColumns = [chevronColumn, nrColumn, nameColumn, exampleColumn, statusColumn, weightColumn, activeColumn];

  const toRows = (defs) =>
    defs
      .map((def) => ({ id: def.number, def, draft: drafts[def.number] }))
      .filter((row) => row.draft);

  // ---- Parameter-Editoren ---------------------------------------------
  const renderUserLimits = (def, rows) => (
    <AppField label={PARAM_LABELS.user_limits} size="md">
      <AppFieldStack size="sm">
        {rows.length === 0 ? (
          <AppText variant="body-2" tone="secondary">Keine Ausnahmen — es gilt das Limit für alle.</AppText>
        ) : null}
        {rows.map((row, index) => (
          <div key={index} style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <div style={{ width: 260 }}>
              <AppSelect
                searchable
                size="sm"
                value={row.userId || ""}
                placeholder="Mitarbeiter wählen"
                options={userOptions.filter(
                  (option) => option.value === row.userId || !rows.some((r) => r.userId === option.value)
                )}
                onChange={(event) => {
                  const next = rows.map((r, i) => (i === index ? { ...r, userId: event.target.value } : r));
                  updateField(def, "user_limits", next);
                }}
              />
            </div>
            <div style={{ width: 220 }}>
              <AppInput
                size="sm"
                type="number"
                value={row.limitText}
                placeholder="leer = unbegrenzt"
                aria-label="Max. Wochenenden"
                onChange={(event) => {
                  const next = rows.map((r, i) => (i === index ? { ...r, limitText: event.target.value } : r));
                  updateField(def, "user_limits", next);
                }}
              />
            </div>
            <AppText variant="body-2" tone="secondary">Wochenenden pro Monat</AppText>
            <AppButton
              variant="muted"
              size="sm"
              onClick={() => updateField(def, "user_limits", rows.filter((_r, i) => i !== index))}
            >
              Entfernen
            </AppButton>
          </div>
        ))}
        <div>
          <AppButton
            variant="outlined"
            size="sm"
            onClick={() => updateField(def, "user_limits", [...rows, { userId: "", limitText: "" }])}
          >
            + Ausnahme hinzufügen
          </AppButton>
        </div>
      </AppFieldStack>
    </AppField>
  );

  const renderIslands = (def, rows) => (
    <AppField label={PARAM_LABELS.islands} size="md">
      <AppFieldStack size="sm">
        {rows.length === 0 ? (
          <AppText variant="body-2" tone="secondary">
            Keine Gruppen definiert — ohne Gruppen ist diese Regel wirkungslos.
          </AppText>
        ) : null}
        {rows.map((row, index) => (
          <div key={index} style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <AppText variant="body-2" tone="secondary">Gruppe</AppText>
            <div style={{ width: 90 }}>
              <AppInput
                size="sm"
                type="number"
                value={row.nrText}
                placeholder="Nr."
                aria-label="Gruppen-Nr"
                onChange={(event) => {
                  const next = rows.map((r, i) => (i === index ? { ...r, nrText: event.target.value } : r));
                  updateField(def, "islands", next);
                }}
              />
            </div>
            <div style={{ minWidth: 320, flex: 1, maxWidth: 480 }}>
              <AppSelect
                multiple
                searchable
                showSelectAll
                size="sm"
                value={row.departmentIds}
                placeholder="Departments wählen"
                options={departmentOptions}
                onChange={(event) => {
                  const next = rows.map((r, i) =>
                    i === index ? { ...r, departmentIds: event.target.value } : r
                  );
                  updateField(def, "islands", next);
                }}
              />
            </div>
            <AppButton
              variant="muted"
              size="sm"
              onClick={() => updateField(def, "islands", rows.filter((_r, i) => i !== index))}
            >
              Entfernen
            </AppButton>
          </div>
        ))}
        <div>
          <AppButton
            variant="outlined"
            size="sm"
            onClick={() => updateField(def, "islands", [...rows, { nrText: String(rows.length + 1), departmentIds: [] }])}
          >
            + Gruppe hinzufügen
          </AppButton>
        </div>
      </AppFieldStack>
    </AppField>
  );

  const renderParamField = (def, descriptor) => {
    const draft = drafts[def.number];
    const { key, kind, defaultValue } = descriptor;
    const label = PARAM_LABELS[key] || key;
    const value = draft.fields[key];

    if (kind === "user_limits") return <div key={key} style={{ gridColumn: "1 / -1" }}>{renderUserLimits(def, value || [])}</div>;
    if (kind === "islands") return <div key={key} style={{ gridColumn: "1 / -1" }}>{renderIslands(def, value || [])}</div>;

    if (kind === "dept_multi" || kind === "shift_multi" || kind === "day_types") {
      const options =
        kind === "dept_multi" ? departmentOptions : kind === "shift_multi" ? shiftOptions : ENUM_OPTIONS.day_types;
      const emptyHint =
        kind === "day_types" && Array.isArray(defaultValue) && defaultValue.length
          ? `leer = Default (${defaultValue
              .map((v) => (ENUM_OPTIONS.day_types.find((o) => o.value === v) || { label: v }).label)
              .join(", ")})`
          : "leer = keine";
      return (
        <AppField key={key} label={label} size="md">
          <AppSelect
            multiple
            searchable
            showSelectAll
            size="sm"
            value={value || []}
            placeholder={emptyHint}
            options={options}
            onChange={(event) => updateField(def, key, event.target.value)}
          />
        </AppField>
      );
    }

    if (kind === "enum") {
      return (
        <AppField key={key} label={label} size="md">
          <AppSelect
            size="sm"
            allowEmpty
            emptyOptionLabel={`Default (${String(defaultValue)})`}
            value={value || ""}
            placeholder={`Default (${String(defaultValue)})`}
            options={ENUM_OPTIONS[key] || []}
            onChange={(event) => updateField(def, key, event.target.value)}
          />
        </AppField>
      );
    }

    if (kind === "bool") {
      return (
        <AppField key={key} label={label} size="md">
          <AppSwitch
            checked={value === null || value === undefined ? Boolean(defaultValue) : Boolean(value)}
            onChange={(event) => updateField(def, key, event.target.checked)}
          />
        </AppField>
      );
    }
    if (kind === "json") {
      return (
        <AppField key={key} label={label} size="md">
          <AppTextarea
            rows={2}
            value={value || ""}
            placeholder={placeholderFor(kind, defaultValue)}
            onChange={(event) => updateField(def, key, event.target.value)}
          />
        </AppField>
      );
    }
    return (
      <AppField key={key} label={label} size="md">
        <AppInput
          size="sm"
          type={kind === "number" ? "number" : "text"}
          value={value || ""}
          placeholder={placeholderFor(kind, defaultValue)}
          onChange={(event) => updateField(def, key, event.target.value)}
        />
      </AppField>
    );
  };

  const renderDetail = (def) => {
    const draft = drafts[def.number];
    if (!draft) return null;
    const saving = savingNb === def.number;
    const descriptors = paramDescriptors(def, draft.fields);

    return (
      <AppFieldStack size="sm">
        <AppText variant="body-2">{def.description}</AppText>
        {def.example ? (
          <AppText variant="body-2" tone="secondary">Beispiel: {def.example}</AppText>
        ) : null}
        {descriptors.length ? (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(260px, 380px))",
              gap: 12,
            }}
          >
            {descriptors.map((descriptor) => renderParamField(def, descriptor))}
          </div>
        ) : (
          <AppText variant="body-2" tone="secondary">
            Keine Parameter — diese Regel hat nur den Aktiv-Schalter{def.kind === "soft" ? " und das Gewicht" : ""}.
          </AppText>
        )}
        {descriptors.length ? (
          <AppText variant="body-2" tone="secondary">Leere Felder = Katalog-Default (grau angezeigt).</AppText>
        ) : null}
        <div style={{ display: "flex", gap: 8 }}>
          <AppButton
            variant="primary"
            size="sm"
            disabled={!draft._dirty || saving}
            onClick={() => saveDraft(def)}
          >
            Speichern
          </AppButton>
          <AppButton
            variant="outlined"
            size="sm"
            disabled={!draft.hasRow || saving}
            onClick={() => resetToDefault(def)}
          >
            {scopeLocationId === null ? "Auf Default zurücksetzen" : "Auf Firmen-Regel zurücksetzen"}
          </AppButton>
        </div>
      </AppFieldStack>
    );
  };

  const renderTable = (defs, columns) => (
    <NbTable
      columns={columns}
      rows={toRows(defs)}
      expandedId={expandedNb}
      onToggle={(id) => setExpandedNb((prev) => (prev === id ? null : id))}
      renderDetail={renderDetail}
    />
  );

  if (loading) {
    return (
      <AppPage>
        <AppWorkspace title="Solver-NB-Verwaltung">
          <AppLoadingOverlay open scope="contained" />
        </AppWorkspace>
      </AppPage>
    );
  }

  return (
    <AppPage>
      <AppWorkspace title="Solver-NB-Verwaltung">
        <AppFieldStack size="md">
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            <div style={{ flex: "1 1 280px", maxWidth: 440 }}>
              <AppField label="Kunde" size="md">
                <AppSelect
                  searchable
                  value={schema}
                  placeholder="Kunde wählen"
                  options={companies.map((c) => ({ value: c.schema_name, label: c.company_name }))}
                  onChange={(event) => setSchema(event.target.value)}
                />
              </AppField>
            </div>
            {schema && locations.length ? (
              <div style={{ flex: "1 1 280px", maxWidth: 440 }}>
                <AppField label="Gilt für" size="md">
                  <AppSelect
                    value={scope}
                    options={[
                      { value: COMPANY_SCOPE, label: "Ganze Firma (Standard)" },
                      ...locations.map((l) => ({ value: String(l.id), label: l.name })),
                    ]}
                    onChange={(event) => setScope(String(event.target.value))}
                  />
                </AppField>
              </div>
            ) : null}
          </div>

          {schema && scopeLocationId !== null ? (
            <AppCallout
              variant="info"
              title="Regeln nur für diesen Standort"
              description="Ohne eigene Einstellung gilt die Firmen-Regel („von Firma“). Speichern legt eine Standort-Regel an, die die Firmen-Regel hier ersetzt. Standorte mit unterschiedlichen Regeln lassen sich nicht gemeinsam berechnen."
            />
          ) : null}

          {!schema ? (
            <AppCallout
              variant="info"
              title="Kunde wählen"
              description="Der Katalog kommt aus der Code-Registry; gespeichert wird nur die Abweichung pro Kunde. Harte Regeln gelten immer (auch ohne Zeile), weiche Regeln laufen nur, wenn sie hier aktiviert sind."
            />
          ) : loadingSettings ? (
            <AppCard>
              <AppCardBody>
                <AppLoadingOverlay open scope="contained" />
              </AppCardBody>
            </AppCard>
          ) : (
            <AppFieldStack size="md">
              <div>
                <AppText as="h2" variant="title-2">Harte Regeln</AppText>
                <AppText as="p" variant="body-2" tone="secondary">
                  Dürfen nie verletzt werden. Ohne Konfiguration aktiv mit Default-Parametern — Schalter aus heisst explizit deaktiviert. Zeile anklicken für Beschreibung und Parameter.
                </AppText>
              </div>
              {renderTable(hardDefs, hardColumns)}

              <div style={{ marginTop: 8 }}>
                <AppText as="h2" variant="title-2">Weiche Regeln</AppText>
                <AppText as="p" variant="body-2" tone="secondary">
                  Kosten Strafpunkte statt zu verbieten — laufen nur, wenn hier aktiviert. Leeres Gewichtsfeld = Katalog-Default (grau angezeigt). Schalter speichert sofort, Gewicht beim Verlassen des Felds.
                </AppText>
              </div>
              {GROUP_ORDER.filter((key) => softByGroup[key]?.length).map((key) => {
                const meta = groupMeta(key);
                return (
                  <AppFieldStack key={key} size="sm">
                    <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <AppText variant="title-3">{meta.label}</AppText>
                      {meta.radar ? (
                        <AppChip variant="neutral" appearance="outlined" size="sm">Radar-Achse</AppChip>
                      ) : null}
                    </span>
                    {renderTable(softByGroup[key], softColumns)}
                  </AppFieldStack>
                );
              })}
            </AppFieldStack>
          )}
        </AppFieldStack>
      </AppWorkspace>
    </AppPage>
  );
}
